import type {
  EditorElement,
  EditorReactComponent,
  ElementItem,
  InlineElement,
} from '@wix/component-protocol/schema';
import { CompRef } from '@wix/document-services-types';

import { EditorElementBuilder } from '@wix/component-protocol/builder';
import { legacySystemComponentsManifests } from '@wix/editor-system-components-legacy';
import { ElementTypeEnumElementType } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { ManifestOverridesResolver } from './ManifestOverridesResolver';

import { AbstractManifestStore } from './AbstractManifestStore';
import { VirtualElement, VirtualElementAPIType } from '../api';

import { DependencyToken, DependenciesContributor } from '../dependencies';
import {
  EditorReactComponentExtensionType,
  IManifestStoreEntity,
  ManifestComponentExtensionType,
  ManifestComponentType,
  ManifestTypeByExtensionType,
  MetadataByExtensionType,
} from './types';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export interface IManifestMiddleware {
  /** Set true on middlewares that handle externally-authored overrides (app manifests, plugins).
   *  Internal platform middlewares leave this unset and bypass override field validation. */
  isExternalMiddleware?: boolean;

  resolveRootElement(
    node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>,
    getElementByPointer: (
      pointer: EditorPointer<CompRef>,
    ) => EditorElement | InlineElement | undefined,
  ): EditorReactComponent | null;

  resolveChildRefElement(
    node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>,
    getElementByPointer: (
      pointer: EditorPointer<CompRef>,
    ) => EditorElement | InlineElement | undefined,
  ): InlineElement | null;
}

const predefinedManifestStore = new Map<
  ManifestComponentType,
  IManifestStoreEntity<EditorReactComponentExtensionType>
>(
  Object.entries(legacySystemComponentsManifests).map(
    ([compType, manifest]) => [
      compType,
      {
        extensionType: ManifestComponentExtensionType.SYSTEM,
        componentType: compType,
        manifest,
        metadata: null,
      },
    ],
  ) as [
    ManifestComponentType,
    IManifestStoreEntity<EditorReactComponentExtensionType>,
  ][],
);

export class ManifestStore extends AbstractManifestStore<EditorReactComponentExtensionType> {
  #listeners: ((compType: string) => void)[] = [];

  #manifestOverrideResolver: ManifestOverridesResolver | null = null;

  constructor(
    private dependenciesContributor: DependenciesContributor,
    private editorPointerAPI: EditorPointerServiceAPI,
  ) {
    super(predefinedManifestStore);
  }

  #getCompType(editorPointer: EditorPointer<CompRef>) {
    const getCompType = this.dependenciesContributor.get(
      DependencyToken.COMP_TYPE_GET,
    );

    return getCompType(editorPointer);
  }

  withManifestOverridesResolver(overrideResolver: ManifestOverridesResolver) {
    this.#manifestOverrideResolver = overrideResolver;
  }

  getStoredRootManifest(editorPointer: EditorPointer<CompRef>) {
    const rootEditorPointer = this.editorPointerAPI.buildEditorPointer(
      this.editorPointerAPI.getDocumentPointer(editorPointer),
    );

    const rootCompType = this.#getCompType(rootEditorPointer);

    if (!rootCompType) {
      return null;
    }

    return this.get(rootCompType);
  }

  getComponentTypeByEditorPointer(editorPointer: EditorPointer<CompRef>) {
    const storedRootManifest = this.getStoredRootManifest(editorPointer);

    if (!storedRootManifest) {
      return null;
    }

    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer) ?? [];

    const isRoot = !innerElementPath.length;

    let componentType = storedRootManifest.componentType;

    if (!isRoot) {
      let element: ElementItem | undefined = {
        elementType: ElementTypeEnumElementType.inlineElement,
        inlineElement: storedRootManifest?.manifest.editorElement,
      };

      innerElementPath.forEach((path) => {
        if (element?.elementType === ElementTypeEnumElementType.refElement) {
          componentType = element?.refElement?.type ?? 'UNKNOWN_CHILD';
          element = element?.refElement?.resolvedElement?.elements?.[path];
        } else if (
          element?.elementType === ElementTypeEnumElementType.inlineElement
        ) {
          element = element?.inlineElement?.elements?.[path];
        }
      });
    }

    return componentType;
  }

  getMetadataByEditorPointer<
    TExtensionType extends ManifestComponentExtensionType,
  >(editorPointer: EditorPointer<CompRef>) {
    return (
      (this.getStoredRootManifest(editorPointer)
        ?.metadata as MetadataByExtensionType[TExtensionType]) ?? null
    );
  }

  getExtensionTypeByEditorPointer(editorPointer: EditorPointer<CompRef>) {
    return this.getStoredRootManifest(editorPointer)?.extensionType ?? null;
  }

  #resolveManifest(editorPointer: EditorPointer<CompRef>) {
    const compType = this.#getCompType(editorPointer);

    if (!compType) {
      return null;
    }

    const storedManifest = this.get(compType);

    if (!storedManifest) {
      return null;
    }

    if (!storedManifest.manifest.editorElement) {
      return storedManifest;
    }

    return {
      ...storedManifest,
      manifest: this.#manifestOverrideResolver
        ? this.#manifestOverrideResolver.resolve(editorPointer, storedManifest)
        : storedManifest.manifest,
    };
  }

  getResolvedEditorElement(
    editorPointer: EditorPointer<CompRef>,
    options?: {
      useRawManifest?: boolean;
    },
  ) {
    const compType = this.#getCompType(editorPointer);

    if (!compType) {
      return null;
    }

    const manifest = options?.useRawManifest
      ? this.get(compType)?.manifest
      : // resolve the root node, then extract by inner element's path
        this.#resolveManifest(
          this.editorPointerAPI.buildEditorPointer(
            this.editorPointerAPI.getDocumentPointer(editorPointer),
          ),
        )?.manifest;

    const elementPath = (editorPointer.innerElementPath ?? []).join('.');

    if (manifest && elementPath.length) {
      return new EditorElementBuilder(manifest?.editorElement).getElementByPath(
        elementPath,
      )!;
    } else {
      return manifest?.editorElement ?? null;
    }
  }

  // xai: should be moved to abstract class
  onManifestLoaded(cb: (compType: string) => void) {
    this.#listeners.push(cb);
  }

  override setManifest(
    compType: string,
    extensionType: EditorReactComponentExtensionType,
    manifest: ManifestTypeByExtensionType[EditorReactComponentExtensionType],
    metadata: MetadataByExtensionType[EditorReactComponentExtensionType],
    isPending?: boolean,
  ) {
    const result = super.setManifest(
      compType,
      extensionType,
      manifest,
      metadata,
      isPending,
    );
    this.#listeners.forEach((cb) => cb(compType));
    return result;
  }
}
